// ============================================================
// ALL IS WELL — WAYFINDING DATA
// For the "I'm Already at All Is Well" in-facility feature.
// IMPORTANT: Do NOT implement fake indoor positioning.
// This is a directional interface only — no fake floor plans.
// Architecture prepared for future QR/BLE/indoor navigation.
// ============================================================

export interface WayfindingDestination {
  id: string;
  label: string;
  description: string;
  icon: string; // Lucide icon name
  floor?: string; // Only if verified
  directions?: WayfindingStep[]; // Simple directional steps
  available: boolean; // false = not available at this centre
}

export interface WayfindingStep {
  step: number;
  instruction: string;
  landmark?: string;
  direction?: 'straight' | 'left' | 'right' | 'up' | 'down';
}

// ============================================================
// STANDARD DESTINATIONS — Available at most centres
// Actual availability filtered by selected centre
// ============================================================
export const wayfindingDestinations: WayfindingDestination[] = [
  {
    id: 'reception',
    label: 'RECEPTION',
    description: 'Registration, enquiries, general assistance',
    icon: 'ConciergeBell',
    available: true,
    directions: [
      { step: 1, instruction: 'You are at the entrance', direction: 'straight' },
      { step: 2, instruction: 'Walk straight ahead', landmark: 'Main entrance', direction: 'straight' },
      { step: 3, instruction: 'Reception is directly ahead', landmark: 'Reception desk', direction: 'straight' },
    ],
  },
  {
    id: 'opd',
    label: 'OPD / DOCTOR',
    description: 'Outpatient department, doctor consultations',
    icon: 'Stethoscope',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception first', direction: 'straight' },
      { step: 2, instruction: 'Collect your OPD token', landmark: 'Reception desk' },
      { step: 3, instruction: 'Follow the OPD signage', direction: 'straight' },
    ],
  },
  {
    id: 'diagnostics',
    label: 'DIAGNOSTICS',
    description: 'Blood tests, imaging, diagnostic services',
    icon: 'FlaskConical',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'Collect your diagnostic request form', landmark: 'Reception desk' },
      { step: 3, instruction: 'Follow the Diagnostics signage', direction: 'right' },
    ],
  },
  {
    id: 'pathology',
    label: 'PATHOLOGY',
    description: 'Blood collection, sample submission',
    icon: 'Microscope',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'Follow the Pathology / Lab signage', direction: 'right' },
    ],
  },
  {
    id: 'pharmacy',
    label: 'PHARMACY',
    description: 'Medicines, prescriptions',
    icon: 'Pill',
    available: true,
    directions: [
      { step: 1, instruction: 'Follow the Pharmacy signage from Reception', direction: 'straight' },
    ],
  },
  {
    id: 'billing',
    label: 'BILLING',
    description: 'Payments, invoices, insurance',
    icon: 'Receipt',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'Ask for the Billing counter', landmark: 'Reception desk' },
    ],
  },
  {
    id: 'emergency',
    label: 'EMERGENCY',
    description: 'Immediate medical attention',
    icon: 'AlertCircle',
    available: true,
    directions: [
      { step: 1, instruction: 'Emergency is at the main entrance', direction: 'straight' },
      { step: 2, instruction: 'Look for the RED Emergency signage', landmark: 'Emergency entrance' },
    ],
  },
  {
    id: 'admission',
    label: 'ADMISSION',
    description: 'Hospital admission, ward allocation',
    icon: 'BedDouble',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'Ask for the Admission desk', landmark: 'Reception desk' },
    ],
  },
  {
    id: 'icu',
    label: 'ICU',
    description: 'Intensive Care Unit — visitor access restricted',
    icon: 'HeartPulse',
    available: false, // Only available at select centres
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'ICU access is restricted — Reception will guide you', landmark: 'Reception desk' },
    ],
  },
  {
    id: 'other',
    label: 'OTHER',
    description: 'Any other destination — ask at Reception',
    icon: 'HelpCircle',
    available: true,
    directions: [
      { step: 1, instruction: 'Proceed to Reception', direction: 'straight' },
      { step: 2, instruction: 'Our team will guide you to your destination', landmark: 'Reception desk' },
    ],
  },
];

// ============================================================
// FUTURE ARCHITECTURE — QR / BLE / Indoor Navigation
// These interfaces are prepared for future implementation.
// Do NOT implement fake indoor positioning now.
// ============================================================

export interface QRLocationToken {
  centreId: string;
  areaId: string;
  timestamp: number;
  // This token should be validated server-side
  // Never expose patient data through QR
}

export interface IndoorLocation {
  centreId: string;
  floor: number;
  areaId: string;
  areaLabel: string;
  // Future: coordinates from BLE beacons or QR scan
}

// Placeholder — to be implemented when QR infrastructure is available
export const parseQRToken = (_token: string): QRLocationToken | null => {
  // TODO: Implement secure QR token parsing
  // This should validate the token server-side
  return null;
};

export const getDestinationById = (id: string): WayfindingDestination | undefined =>
  wayfindingDestinations.find((d) => d.id === id);

export const getAvailableDestinations = (centreType?: string): WayfindingDestination[] => {
  // For now, return all available destinations
  // Future: filter by centre capabilities
  if (centreType === 'collection-center' || centreType === 'help-center') {
    return wayfindingDestinations.filter((d) =>
      ['reception', 'pathology', 'billing', 'other'].includes(d.id)
    );
  }
  return wayfindingDestinations.filter((d) => d.available);
};
